<template>
  <el-dialog
    title="支付提示"
    :visible.sync="dialogVisible"
    width="360px"
    :before-close="handleClose"
    center
  >
    <div class="payment-dialog-content">
      <!-- 账户余额 -->
      <div class="payment-row">
        <span class="row-label">账户余额：</span>
        <span class="row-value">{{ balance }}</span>
        <span class="row-unit">元</span>
      </div>

      <!-- 本次支付金额 -->
      <div class="payment-row">
        <span class="row-label">本次支付金额：</span>
        <span class="row-value highlight">{{ paymentInfo.amount }}</span>
        <span class="row-unit">元</span>
      </div>

      <div class="payment-notice">
        {{ paymentInfo.notice }}
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleAlipayPay">支付宝支付</el-button>
      <el-button type="primary" @click="handleBalancePay">余额支付</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { toNumber } from '@/utils/index'
import { mapGetters } from 'vuex'

const DEFAULT_PAYMENT_NOTICE = '数字版权登记费用一旦支付即无法更换著作权人并且无法退款，继续支付代表同意上述条款。'

/**
 * 支付弹窗组件
 * 
 * 使用方式：
 * 1. 引入组件: import PaymentDialog from '@/components/PaymentDialog.vue'
 * 2. 注册组件: components: { PaymentDialog }
 * 3. 使用组件: <PaymentDialog ref="paymentDialog" />
 * 4. 打开弹窗: this.$refs.paymentDialog.open({ amount: '20.00', onAlipay: fn, onBalance: fn })
 * 
 * 注：账户余额自动从 Vuex store 获取，无需传入
 */
export default {
  name: 'PaymentDialog',
  data() {
    return {
      dialogVisible: false,
      resolve: null,
      reject: null,
      // 支付信息 - 通过 open 方法传入
      paymentInfo: {
        amount: '0.00',     // 本次支付金额
        rowList: [],        // 用于回调时传递
        notice: DEFAULT_PAYMENT_NOTICE, // 支付提示语
        onAlipay: null,     // 支付宝支付回调函数
        onBalance: null     // 余额支付回调函数
      }
    }
  },
  computed: {
    ...mapGetters(['balance'])
  },
  methods: {
    // 刷新用户余额
    refreshBalance() {
      return this.$store.dispatch('GetBalance')
    },
    /**
     * 打开支付弹窗
     * @param {Object} options 支付信息
     * @param {string} options.amount 本次支付金额
     * @param {Array} options.rowList 用于回调时传递
     * @param {Function} options.onAlipay 支付宝支付回调函数
     * @param {Function} options.onBalance 余额支付回调函数
     */
    open(options = {}) {
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
        this.paymentInfo = {
          amount: options.amount || '0.00',
          rowList: options.rowList || [],
          notice: options.notice || DEFAULT_PAYMENT_NOTICE,
          onAlipay: options.onAlipay || null,
          onBalance: options.onBalance || null
        }
        this.dialogVisible = true
      })
    },

    // 关闭弹窗
    close() {
      this.dialogVisible = false
      if(this.reject) {
        this.reject()
        this.resolve = null
        this.reject = null
      }
    },

    // 关闭前处理
    handleClose(done) {
      done()
    },

    // 支付宝支付
    async handleAlipayPay() {
      try {
        await this.paymentInfo?.onAlipay?.({ ...this.paymentInfo })
        if(this.resolve) {
          this.resolve()
          this.resolve = null
          this.reject = null
        }
      } catch (error) {
        this.$message.error('支付宝支付失败')
      } finally {
        this.close()
      }
    },

    // 余额支付
    async handleBalancePay() {
      // 检查余额是否充足
      const balance = toNumber(this.balance)
      const amount = toNumber(this.paymentInfo.amount)
      
      if (balance < amount) {
        this.$message.warning('账户余额不足，请先充值或使用支付宝支付')
        return
      }
      try {
        await this.paymentInfo?.onBalance?.({ ...this.paymentInfo })
        // 余额支付成功后刷新余额
        this.refreshBalance()
        if(this.resolve) {
          this.resolve()
          this.resolve = null
          this.reject = null
        }
      } catch (error) {
        this.$message.error('余额支付失败')
      } finally {
        this.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-dialog__body {
    padding: 12px 24px !important;
  }

.payment-dialog-content {
  padding: 0px 0;
}

.payment-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  .row-label {
    font-size: 14px;
    color: #666;
    text-align: right;
    width: 100px;
  }

  .row-value {
    font-size: 18px;
    color: #333;
    font-weight: bold;
    margin: 0 5px;

    &.highlight {
      font-size: 24px;
      color: #409EFF;
    }
  }

  .row-unit {
    font-size: 14px;
    color: #666;
  }
}

.payment-notice {
  width: 280px;
  margin: 0 auto;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #f56c6c;
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
  text-align: left;
  white-space: pre-line;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 20px;
}
</style>

